import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import MDEditor from '@uiw/react-md-editor';
import { 
  Video, Clock, Award, CheckCircle2, Play, 
  AlertCircle, Shield, Heart, User, Flame, Zap, Info,
  HardHat, Trophy, Eye, ChevronDown, ChevronUp,
  RefreshCw, ExternalLink, UserCircle, Briefcase,
  FileText, BookOpen
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Category icons mapping
const getCategoryIcon = (categoryId) => {
  const icons = {
    seguranca: Shield,
    saude: Heart,
    ergonomia: User,
    epi: HardHat,
    incendio: Flame,
    primeiros_socorros: AlertCircle,
    quimico: Info,
    eletrico: Zap,
    geral: Info
  };
  const Icon = icons[categoryId] || Info;
  return Icon;
};

// Video Player Component with Progress Tracking
const VideoPlayer = ({ videoInfo, videoUrl, onProgressReached, progressThreshold = 90 }) => {
  const videoRef = useRef(null);
  const iframeRef = useRef(null);
  const [watchProgress, setWatchProgress] = useState(0);
  const [hasReachedThreshold, setHasReachedThreshold] = useState(false);
  const progressIntervalRef = useRef(null);
  
  // Track progress for MP4 videos
  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      const video = videoRef.current;
      const progress = (video.currentTime / video.duration) * 100;
      setWatchProgress(Math.round(progress));
      
      if (progress >= progressThreshold && !hasReachedThreshold) {
        setHasReachedThreshold(true);
        onProgressReached?.();
      }
    }
  }, [progressThreshold, hasReachedThreshold, onProgressReached]);
  
  // For YouTube/Vimeo, we simulate progress based on time spent watching
  // Since we can't access iframe video progress directly due to CORS
  useEffect(() => {
    if (videoInfo && (videoInfo.type === 'youtube' || videoInfo.type === 'vimeo')) {
      // Start tracking time when iframe is mounted
      const startTime = Date.now();
      const estimatedDuration = 5 * 60 * 1000; // Assume 5 min video if not specified
      
      progressIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(100, (elapsed / estimatedDuration) * 100);
        setWatchProgress(Math.round(progress));
        
        if (progress >= progressThreshold && !hasReachedThreshold) {
          setHasReachedThreshold(true);
          onProgressReached?.();
        }
      }, 1000);
      
      return () => {
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
        }
      };
    }
  }, [videoInfo, progressThreshold, hasReachedThreshold, onProgressReached]);
  
  if (videoInfo && (videoInfo.type === 'youtube' || videoInfo.type === 'vimeo')) {
    return (
      <div className="space-y-2">
        <div className="relative w-full pb-[56.25%] h-0 overflow-hidden rounded-lg bg-slate-900">
          <iframe
            ref={iframeRef}
            className="absolute top-0 left-0 w-full h-full"
            src={videoInfo.embed_url}
            title="Video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        {/* Progress indicator for embedded videos */}
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-amber-500 transition-all duration-500"
              style={{ width: `${watchProgress}%` }}
            />
          </div>
          <span>{watchProgress}%</span>
          {hasReachedThreshold && (
            <CheckCircle2 className="w-4 h-4 text-green-500" />
          )}
        </div>
      </div>
    );
  }
  
  if (videoUrl && videoUrl.includes('.mp4')) {
    return (
      <div className="space-y-2">
        <div className="relative w-full pb-[56.25%] h-0 overflow-hidden rounded-lg bg-slate-900">
          <video
            ref={videoRef}
            className="absolute top-0 left-0 w-full h-full"
            controls
            src={videoUrl}
            onTimeUpdate={handleTimeUpdate}
          >
            Seu navegador não suporta o elemento de vídeo.
          </video>
        </div>
        {/* Progress indicator for MP4 videos */}
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-amber-500 transition-all duration-500"
              style={{ width: `${watchProgress}%` }}
            />
          </div>
          <span>{watchProgress}%</span>
          {hasReachedThreshold && (
            <CheckCircle2 className="w-4 h-4 text-green-500" />
          )}
        </div>
      </div>
    );
  }
  
  return null;
};

// Professional Spotlight Card Component
const ProfessionalSpotlight = ({ dds }) => {
  const hasAuthorData = dds?.autor_nome || dds?.autor_especialidade;
  
  if (!hasAuthorData) return null;
  
  const handleContact = () => {
    if (dds.autor_link) {
      window.open(dds.autor_link, '_blank', 'noopener,noreferrer');
    }
  };
  
  return (
    <div 
      className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 rounded-xl border border-amber-200 dark:border-amber-800"
      data-testid="professional-spotlight-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="p-1.5 bg-amber-500 rounded-lg">
          <Trophy className="w-4 h-4 text-white" />
        </div>
        <h4 className="font-semibold text-amber-800 dark:text-amber-300 text-sm">
          Destaque do Profissional
        </h4>
      </div>
      
      <div className="flex items-start gap-4">
        {/* Author Photo or Avatar */}
        <div className="flex-shrink-0">
          {dds.autor_foto_url ? (
            <img 
              src={dds.autor_foto_url} 
              alt={dds.autor_nome}
              className="w-16 h-16 rounded-full object-cover border-2 border-amber-300"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
              <UserCircle className="w-10 h-10 text-white" />
            </div>
          )}
        </div>
        
        {/* Author Info */}
        <div className="flex-1 min-w-0">
          {dds.autor_nome && (
            <h5 className="font-bold text-slate-800 dark:text-white text-lg truncate">
              {dds.autor_nome}
            </h5>
          )}
          
          {dds.autor_especialidade && (
            <div className="flex items-center gap-1.5 mt-1 text-slate-600 dark:text-slate-400">
              <Briefcase className="w-4 h-4" />
              <span className="text-sm">{dds.autor_especialidade}</span>
            </div>
          )}
          
          {/* Contact Button */}
          {dds.autor_link && (
            <button
              onClick={handleContact}
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors"
              data-testid="contact-professional-btn"
            >
              <ExternalLink className="w-4 h-4" />
              Entrar em Contato
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const MeusDDS = () => {
  const { user } = useAuth();
  const [ddsData, setDdsData] = useState({ dds_list: [], por_categoria: {}, categorias: [] });
  const [stats, setStats] = useState({ total_disponiveis: 0, assistidos: 0, pendentes: 0, pontos_ganhos: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedDDS, setSelectedDDS] = useState(null);
  const [showPlayer, setShowPlayer] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [completing, setCompleting] = useState(false);
  const [canComplete, setCanComplete] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ddsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/employee-portal/dds`),
        axios.get(`${API_URL}/employee-portal/dds/stats`)
      ]);
      
      setDdsData(ddsRes.data);
      setStats(statsRes.data);
      
      // Expand first category by default
      const categories = Object.keys(ddsRes.data.por_categoria || {});
      if (categories.length > 0) {
        setExpandedCategories({ [categories[0]]: true });
      }
    } catch (error) {
      console.error('Erro ao carregar DDS:', error);
      toast.error('Erro ao carregar seus DDS');
    } finally {
      setLoading(false);
    }
  };

  const handleWatchDDS = (dds) => {
    setSelectedDDS(dds);
    setShowPlayer(true);
    // For text DDS, allow completion immediately (no video progress to track)
    // For video DDS, require 90% watch progress (unless already watched)
    if (dds.tipo_conteudo === 'texto') {
      setCanComplete(true); // Text DDS can be completed immediately after reading
    } else {
      setCanComplete(dds.assistido || false); // Video: Already watched = can close anytime
    }
  };

  // Called when video reaches 90% progress
  const handleProgressReached = useCallback(() => {
    setCanComplete(true);
    toast.success('90% assistido! Você pode concluir o DDS agora.', {
      icon: '🎉',
      duration: 3000
    });
  }, []);

  const handleCompleteDDS = async () => {
    if (!selectedDDS || completing) return;
    
    setCompleting(true);
    
    try {
      const response = await axios.post(`${API_URL}/employee-portal/dds/${selectedDDS.id}/view`);
      
      if (response.data.already_viewed) {
        toast.info('Você já assistiu este DDS anteriormente');
      } else {
        toast.success(response.data.message);
      }
      
      setShowPlayer(false);
      setSelectedDDS(null);
      fetchData();
    } catch (error) {
      toast.error('Erro ao registrar visualização');
    } finally {
      setCompleting(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const getCategoryName = (categoryId) => {
    const cat = ddsData.categorias?.find(c => c.id === categoryId);
    return cat?.nome || categoryId;
  };

  const getCategoryColor = (categoryId) => {
    const cat = ddsData.categorias?.find(c => c.id === categoryId);
    return cat?.cor || '#6b7280';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="meus-dds-page">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
          Meus DDS
        </h2>
        <p className="text-slate-600 dark:text-slate-400 mt-1">
          Olá, {user?.name || 'Funcionário'}! Assista aos vídeos de segurança e ganhe pontos.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Video size={24} />
              <div>
                <p className="text-sm opacity-80">Total DDS</p>
                <p className="text-2xl font-bold">{stats.total_disponiveis}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={24} />
              <div>
                <p className="text-sm opacity-80">Assistidos</p>
                <p className="text-2xl font-bold">{stats.assistidos}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle size={24} />
              <div>
                <p className="text-sm opacity-80">Pendentes</p>
                <p className="text-2xl font-bold">{stats.pendentes}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Trophy size={24} />
              <div>
                <p className="text-sm opacity-80">Pontos Ganhos</p>
                <p className="text-2xl font-bold">{stats.pontos_ganhos}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progress */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="font-medium">Progresso Geral</span>
          <span className="text-sm text-slate-500">{stats.taxa_conclusao}% concluído</span>
        </div>
        <Progress value={stats.taxa_conclusao} className="h-3" />
      </Card>

      {/* DDS by Category */}
      {Object.keys(ddsData.por_categoria || {}).length === 0 ? (
        <Card className="p-12 text-center">
          <Video size={48} className="mx-auto text-slate-400 mb-4" />
          <h3 className="text-lg font-semibold text-slate-600 mb-2">
            Nenhum DDS disponível
          </h3>
          <p className="text-slate-500">
            Não há vídeos de segurança disponíveis no momento.
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {Object.entries(ddsData.por_categoria).map(([categoryId, ddsList]) => {
                const CategoryIcon = getCategoryIcon(categoryId);
                const isExpanded = expandedCategories[categoryId];
                const completedCount = ddsList.filter(d => d.assistido).length;
                
                return (
                  <Card key={categoryId} className="overflow-hidden">
                    {/* Category Header */}
                    <button
                  onClick={() => toggleCategory(categoryId)}
                  className="w-full p-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                  data-testid={`category-${categoryId}`}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: getCategoryColor(categoryId) + '20' }}
                    >
                      <CategoryIcon 
                        className="w-5 h-5" 
                        style={{ color: getCategoryColor(categoryId) }}
                      />
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold">{getCategoryName(categoryId)}</h3>
                      <p className="text-sm text-slate-500">
                        {completedCount} de {ddsList.length} assistidos
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Progress 
                      value={(completedCount / ddsList.length) * 100} 
                      className="w-24 h-2"
                    />
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </button>
                
                {/* DDS List */}
                {isExpanded && (
                  <div className="border-t divide-y">
                    {ddsList.map(dds => (
                      <div 
                        key={dds.id}
                        className={`p-4 flex items-center gap-4 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors ${dds.assistido ? 'bg-green-50 dark:bg-green-950/20' : ''}`}
                        data-testid={`dds-item-${dds.id}`}
                      >
                        {/* Thumbnail */}
                        <div className="relative w-32 h-20 flex-shrink-0 bg-slate-200 dark:bg-slate-700 rounded-lg overflow-hidden">
                          {dds.video_info?.thumbnail ? (
                            <img 
                              src={dds.video_info.thumbnail} 
                              alt={dds.titulo}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Video className="w-8 h-8 text-slate-400" />
                            </div>
                          )}
                          
                          {/* Play button overlay */}
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                            {dds.assistido ? (
                              <CheckCircle2 className="w-8 h-8 text-green-500" />
                            ) : (
                              <Play className="w-8 h-8 text-white" />
                            )}
                          </div>
                          
                          {/* Duration */}
                          <div className="absolute bottom-1 right-1">
                            <Badge className="bg-black/70 text-white text-xs px-1.5 py-0.5">
                              {dds.duracao_minutos}min
                            </Badge>
                          </div>
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium truncate">{dds.titulo}</h4>
                            {dds.assistido && (
                              <Badge className="bg-green-500 text-xs">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Concluído
                              </Badge>
                            )}
                          </div>
                          
                          {dds.descricao && (
                            <p className="text-sm text-slate-500 truncate mb-2">{dds.descricao}</p>
                          )}
                          
                          <div className="flex items-center gap-3 text-sm">
                            <span className="flex items-center gap-1 text-slate-400">
                              <Clock className="w-3 h-3" />
                              {dds.duracao_minutos} min
                            </span>
                            <Badge variant="outline" className="text-amber-600 border-amber-600 text-xs">
                              <Award className="w-3 h-3 mr-1" />
                              +{dds.pontos} pts
                            </Badge>
                            {dds.total_visualizacoes > 0 && (
                              <span className="flex items-center gap-1 text-slate-400 text-xs">
                                <Eye className="w-3 h-3" />
                                {dds.total_visualizacoes}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Action */}
                        <Button
                          onClick={() => handleWatchDDS(dds)}
                          className={dds.assistido ? 'bg-slate-500 hover:bg-slate-600' : 'bg-amber-600 hover:bg-amber-700'}
                          size="sm"
                          data-testid={`watch-dds-${dds.id}`}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          {dds.assistido ? 'Rever' : 'Assistir'}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Video Player Modal */}
      <Dialog open={showPlayer} onOpenChange={(open) => {
        if (!open) {
          setCanComplete(false);
        }
        setShowPlayer(open);
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedDDS?.tipo_conteudo === 'texto' ? (
                <FileText className="w-5 h-5 text-purple-500" />
              ) : (
                <Video className="w-5 h-5 text-amber-500" />
              )}
              {selectedDDS?.titulo}
            </DialogTitle>
          </DialogHeader>
          
          {selectedDDS && (
            <div className="space-y-4">
              {/* Content based on type */}
              {selectedDDS.tipo_conteudo === 'texto' ? (
                /* TEXT DDS Content with Markdown support */
                <div className="space-y-4">
                  {/* Description */}
                  {selectedDDS.descricao && (
                    <p className="text-slate-600 dark:text-slate-400 italic border-l-4 border-purple-500 pl-4">
                      {selectedDDS.descricao}
                    </p>
                  )}
                  
                  {/* Markdown Content - Renders with images */}
                  <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 sm:p-6 border overflow-x-hidden" data-color-mode="light">
                    <MDEditor.Markdown 
                      source={selectedDDS.conteudo_texto || ''}
                      className="dds-content-display"
                      style={{
                        backgroundColor: 'transparent',
                        fontSize: '15px',
                        lineHeight: '1.7'
                      }}
                    />
                  </div>
                  
                  {/* Reading Progress Helper */}
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                    <div className="flex items-center gap-3">
                      <BookOpen className="w-5 h-5 text-purple-600" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-purple-800 dark:text-purple-300">
                          Leitura estimada: {selectedDDS.duracao_minutos} minutos
                        </p>
                        <p className="text-xs text-purple-600 dark:text-purple-400">
                          Leia o conteúdo completo e clique em "Concluir Leitura" para ganhar {selectedDDS.pontos} pontos
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* VIDEO DDS Content */
                <>
                  {/* Video with Progress Tracking */}
                  <VideoPlayer 
                    videoInfo={selectedDDS.video_info} 
                    videoUrl={selectedDDS.video_url}
                    onProgressReached={handleProgressReached}
                    progressThreshold={90}
                  />
                  
                  {/* Description */}
                  {selectedDDS.descricao && (
                    <p className="text-slate-600 dark:text-slate-400">{selectedDDS.descricao}</p>
                  )}
                </>
              )}
              
              {/* Info */}
              <div className="flex items-center gap-4 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {selectedDDS.duracao_minutos} minutos
                </span>
                <Badge variant="outline" className="text-amber-600 border-amber-600">
                  <Award className="w-3 h-3 mr-1" />
                  +{selectedDDS.pontos} pontos
                </Badge>
                <Badge variant="outline" className={selectedDDS.tipo_conteudo === 'texto' ? 'text-purple-600 border-purple-600' : 'text-red-600 border-red-600'}>
                  {selectedDDS.tipo_conteudo === 'texto' ? (
                    <><FileText className="w-3 h-3 mr-1" />Texto</>
                  ) : (
                    <><Video className="w-3 h-3 mr-1" />Vídeo</>
                  )}
                </Badge>
              </div>
              
              {/* Professional Spotlight Card */}
              <ProfessionalSpotlight dds={selectedDDS} />
              
              {/* Progress hint for unfinished videos (only show for video type) */}
              {!selectedDDS.assistido && !canComplete && selectedDDS.tipo_conteudo !== 'texto' && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                  <Info className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Assista pelo menos <strong>90% do vídeo</strong> para poder concluir o DDS e ganhar seus pontos.
                  </p>
                </div>
              )}
              
              {/* Complete Button */}
              <div className="flex justify-end pt-4 border-t">
                {selectedDDS.assistido ? (
                  <Button variant="outline" onClick={() => setShowPlayer(false)}>
                    <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />
                    Já Concluído - Fechar
                  </Button>
                ) : (
                  <Button 
                    onClick={handleCompleteDDS}
                    className={canComplete 
                      ? "bg-green-600 hover:bg-green-700" 
                      : "bg-slate-400 cursor-not-allowed"
                    }
                    disabled={completing || !canComplete}
                    data-testid="complete-dds-btn"
                  >
                    {completing ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Registrando...
                      </>
                    ) : canComplete ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        {selectedDDS.tipo_conteudo === 'texto' ? 'Concluir Leitura' : 'Concluir DDS'} (+{selectedDDS.pontos} pts)
                      </>
                    ) : (
                      <>
                        <Clock className="w-4 h-4 mr-2" />
                        Aguardando 90%...
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MeusDDS;
